import type { PerformanceResourceItem, PromotionApi } from '@/app/lib/api/types-api'
import { filterVisiblePromotions } from '@/app/lib/pricing'
import { sanitizeHtml } from '@/app/lib/utils/sanitizeHtml'

import type { PerformanceInfoNoteView } from './resolveInfoNotes'
import { resolveInfoNotes } from './resolveInfoNotes'
import type { PerformanceViewOptions } from './types'

/** Promociones visibles antes de plegar el resto tras el botón (FR-026). */
const VISIBLE_PROMOTIONS_LIMIT = 4

export type { PerformanceInfoNoteView }

export interface PerformancePromoViewModel {
  /** Promoción que prevalece sobre el resto; `null` si no llega (FR-023). */
  readonly main: PromotionApi | null
  /** Avisos, justo tras la destacada y arriba de la ficha (FR-024). */
  readonly notes: readonly PerformanceInfoNoteView[]
  /** HTML saneado del punto de venta (canales expertos), tras los avisos; `null` si no hay. */
  readonly pointSale: string | null
  /** Las primeras 4 del resto (FR-026). */
  readonly visible: readonly PromotionApi[]
  /** Cuántas quedan plegadas tras «Todas las promociones (N)». */
  readonly hiddenCount: number
  /** Las plegadas, ya listas para revelarse sin otra petición. */
  readonly hidden: readonly PromotionApi[]
  /** Hay algo para el bloque superior: destacada, avisos o punto de venta. */
  readonly hasFeatured: boolean
  /** Hay algo para la lista tras la galería: promociones visibles o plegadas. */
  readonly hasRest: boolean
  /** `true` si no hay absolutamente nada que pintar (ni `hasFeatured` ni `hasRest`). */
  readonly isEmpty: boolean
}

/**
 * Aplica el criterio de visibilidad ya existente **conservando la forma cruda**.
 *
 * `filterVisiblePromotions` devuelve `Promotion` normalizada (`id`, `price`,
 * `promotionLabel`, `isSpecial`), que descarta `title` y `description` — justo
 * lo que este bloque necesita pintar. Correlacionar su salida con la entrada
 * por `id` no sirve: en el payload real muchas promociones traen `"id": ""`.
 *
 * Se resuelve pasando cada promoción por el filtro **de una en una**: se
 * reutiliza la regla exacta (ventana de fechas incluida), sin duplicarla ni
 * depender de identificadores que el backend no garantiza únicos.
 */
function filterVisibleRaw(promotions: readonly PromotionApi[], now: Date): PromotionApi[] {
  return promotions.filter((promotion) => filterVisiblePromotions([promotion], now).length > 0)
}

/**
 * ¿Son la misma promoción? Se comparan los campos de contenido, no el `id`:
 * `promotion_main` llega duplicada dentro de `promotions` en el payload real
 * (caso «El Rey León») y ambas copias traen `"id": ""`.
 */
function isSamePromotion(a: PromotionApi, b: PromotionApi): boolean {
  return a.title === b.title && a.description === b.description && a.id === b.id
}

/**
 * Modelo de vista del bloque de promoción destacada + avisos + promociones
 * (data-model.md §4).
 *
 * Orden de render fijo, no negociable: **arriba** destacada → avisos → punto de
 * venta; **tras la galería** el resto (FR-023/FR-024/FR-025; el punto de venta
 * solo en canales, specs/059-canales-pdp). `hasFeatured`/`hasRest` gobiernan
 * cada uno de los dos bloques.
 *
 * Ojo con una consecuencia deliberada del criterio de visibilidad compartido:
 * las promociones sin campo `visible` se descartan **todas**, así que una ficha
 * con doce promociones en el payload puede legítimamente no mostrar ninguna. Es
 * el mismo comportamiento que ya tienen las tarjetas de listado, y mantenerlo es
 * justamente lo que evita que ficha y tarjeta discrepen (SC-005).
 */
export function toPerformancePromoViewModel(
  resource: Pick<
    PerformanceResourceItem,
    'promotion_main' | 'promotions' | 'info_notes' | 'point_sale_information'
  >,
  now: Date = new Date(),
  options: PerformanceViewOptions = {},
): PerformancePromoViewModel {
  const main = resource.promotion_main

  const rest = filterVisibleRaw(resource.promotions ?? [], now).filter(
    (promotion) => !main || !isSamePromotion(promotion, main),
  )

  // Compartido con `hasSpecialConditions`, que decide si el hero anuncia estos
  // mismos avisos — ver el JSDoc de `resolveInfoNotes`.
  const notes = resolveInfoNotes(resource.info_notes, { now: options.now ?? now })

  const pointSale = options.includePointSale
    ? sanitizeHtml(resource.point_sale_information ?? '') || null
    : null

  const visible = rest.slice(0, VISIBLE_PROMOTIONS_LIMIT)
  const hidden = rest.slice(VISIBLE_PROMOTIONS_LIMIT)

  const hasFeatured = main !== null || notes.length > 0 || pointSale !== null
  const hasRest = rest.length > 0

  return {
    main,
    notes,
    pointSale,
    visible,
    hidden,
    hiddenCount: hidden.length,
    hasFeatured,
    hasRest,
    isEmpty: !hasFeatured && !hasRest,
  }
}
